import { Input, type NumberInputProps } from "@mantine/core";
import { type ReactNode, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { useInputProps } from "../hooks/useInputProps";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import useSavedCursorPosition from "../../hooks/useSavedCursorPosition";
import { getClassName } from "../CustomInput/CustomInput.utils";
import useNMEAAutomated from "../hooks/useNMEAAutomated";
import useDPFHoursCalculation from "../CustomInput/useDPFHoursCalculation";
import useFWCalculation from "../CustomInput/useFWCalculation";
import { useValorCalc } from "./useValorCalc";
import { useTheme } from "src/hooks";

interface INumberInput extends NumberInputProps {
  id: string;
  width?: string;
  required?: boolean;
  suffix?: string | ReactNode;
  additionalOnChange?: (value: any, id?: string) => void;
  additionalOnBlur?: (e: any) => void;
  rowIdx?: number;
  tag?: string;
  source?: "auto" | "manual";
  sectionIndex?: number;
  name?: string;
  autoMinus?: boolean;
  numberOfCols?: number;
  totalRows?: number;
  sectionTag?: string;
}

const CustomNumberInput = ({
  id,
  value,
  suffix,
  required,
  width,
  additionalOnChange,
  additionalOnBlur,
  rowIdx,
  tag,
  name,
  source,
  sectionIndex,
  autoMinus,
  numberOfCols,
  totalRows,
  sectionTag,
  ...props
}: INumberInput) => {
  const theme = useTheme() as any;
  const { onChange, ...inputProps } = useInputProps(id, value, required);
  const { setValue } = useFormContext();
  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue: value,
    name,
  });

  // DPF hours calculation hook
  useDPFHoursCalculation({
    id,
    tag,
    setValue,
  });
  // Fresh Water calculation hook
  useFWCalculation({
    id,
    tag,
    setValue,
  });
  // Valor Total Fuel calculation hook
  useValorCalc({
    sectionTag,
    tag,
    rowIndex: rowIdx,
    id,
    setValue,
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const logbookRefsProps = useLogbookRefsContext(
    id,
    numberOfCols,
    rowIdx,
    totalRows,
  );
  const prevValue = useRef(value?.toString());

  const { handleFocus, handleBlur, setCursorPosition } = useSavedCursorPosition(
    id,
    inputRef,
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    if (inputValue === "" || /^[+-]?\d*(\.\d*)?$/.test(inputValue)) {
      if (
        autoMinus &&
        prevValue.current?.toString() === "" &&
        ![".", ",", "-"].includes(inputValue)
      ) {
        inputValue = `-${inputValue}`;
      }
      event.target.value = inputValue;
      prevValue.current = inputValue;
      setCursorPosition();
      additionalOnChange?.(inputValue, id);
      onChange(inputValue);
    }
  };

  const shouldHighlightValue = (
    tag: string | undefined,
    value: any,
  ): boolean => {
    // if (tag === "valorC" && parseFloat(value) < 0) return true;
    return false;
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    handleNMEAChange(inputProps.value);

    handleBlur(event);
    additionalOnBlur?.(event);
  };
  return (
    <Input
      id={id}
      className={getClassName(tag, rowIdx)}
      miw={Number(width) * 10}
      {...props}
      {...inputProps}
      ref={el => {
        inputProps.ref(el);
        inputRef.current = el;
        logbookRefsProps.ref(el);
      }}
      styles={{
        input: {
          color: shouldHighlightValue(tag, value)
            ? theme.colors.redText
            : "inherit",
        },
      }}
      onKeyDown={logbookRefsProps.onKeyDown}
      onFocus={handleFocus}
      onBlur={handleInputBlur}
      onChange={handleChange}
      rightSection={suffix}
      rightSectionProps={{
        style: {
          width: "max-content",
          padding: "0 10px",
        },
      }}
    />
  );
};

export default CustomNumberInput;
